import { ApolloError } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ViewerContext } from "dash-ui";
import {
  useGetFullAccountQuery,
  useTemporaryAuthenticationMutation,
} from "gql/generated.graphql";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AuthPagesLayout } from "v2/components/auth-pages-layout";
import { z } from "zod";

const signInFormSchema = z.object({
  email: z.string().min(1, "Email is required.").email("Email must be valid."),
  password: z.string().min(1, "Password is required."),
});

type SignInFormProps = z.infer<typeof signInFormSchema>;

export function SignInPage() {
  // React states
  const [error, setError] = useState("");

  // Query and Mutations
  const getAccountQuery = useGetFullAccountQuery();
  const [tempAuthMutation] = useTemporaryAuthenticationMutation();

  // Hooks
  const navigate = useNavigate();
  const auth = useContext(ViewerContext);

  // Constants
  const signInForm = useForm<SignInFormProps>({
    resolver: zodResolver(signInFormSchema),
  });

  // Handle sign in functionality
  async function signIn(data: SignInFormProps) {
    try {
      const result = await tempAuthMutation({
        variables: { email: data.email, password: data.password },
      });
      const tempToken = result.data?.temporaryAuthentication.tempToken;
      if (!tempToken) throw new Error("Token was not provided by API.");
      localStorage.setItem("ACCESS_TOKEN", tempToken);
      const account = await (await getAccountQuery.refetch()).data.getAccount;
      if (auth.setAccount) auth.setAccount(account);
      navigate("/auth/sign-in/mfa");
    } catch (error) {
      if ((error as ApolloError).message === "INCORRECT_ACCOUNT_CREDENTIALS") {
        setError("Incorrect credentials.");
        return;
      }
      setError((error as Error).message);
    }
  }

  // Redirect if user already logged in
  useEffect(() => {
    const accessToken = localStorage.getItem("ACCESS_TOKEN");
    const refreshToken = localStorage.getItem("REFRESH_TOKEN");
    if (accessToken && !refreshToken) return navigate("/auth/sign-in/mfa");
    if (accessToken && refreshToken) return navigate("/properties");
  }, []);

  // Return HTML element
  return (
    <AuthPagesLayout>
      <div className="flex flex-col items-center justify-center h-full w-full">
        <form
          className="flex flex-col w-full max-w-80"
          onSubmit={signInForm.handleSubmit(signIn)}>
          <h1 className="text-3xl font-[montserrat] font-bold text-slate-800 mt-1">
            Welcome to DASH!
          </h1>
          <p className="text-slate-500">Sign in to your account.</p>
          <label className="text-sm text-slate-500 mt-6">Email</label>
          <input
            type="email"
            placeholder="john@dash.com"
            className={`h-10 border ${
              signInForm.formState.errors.email
                ? "border-red-300 bg-red-50"
                : "border-slate-300"
            } rounded px-4`}
            {...signInForm.register("email")}
          />
          <span className="text-sm text-red-600">
            {signInForm.formState.errors.email?.message}
          </span>
          <label className="text-sm text-slate-500 mt-2">Password</label>
          <input
            type="password"
            placeholder="Enter password"
            className={`h-10 border ${
              signInForm.formState.errors.password
                ? "border-red-300 bg-red-50"
                : "border-slate-300"
            } rounded px-4`}
            {...signInForm.register("password")}
          />
          <span className="text-sm text-red-600">
            {signInForm.formState.errors.password?.message}
          </span>
          {error && (
            <span className="border border-red-300 bg-red-50 h-10 px-4 rounded flex items-center text-sm text-red-600 mt-4">
              ⚠️ {error}
            </span>
          )}
          <button
            type="submit"
            className="h-10 bg-sky-600 text-white rounded mt-4">
            Login
          </button>
          <a className="mt-8">Forgot your password?</a>
        </form>
      </div>
    </AuthPagesLayout>
  );
}
